export enum LicenseType {
  None = 0,
  Basic = 1,
  Ogi = 2,
  Custom1 = 3,
  Custom2 = 4,
  Custom3 = 5,
  Custom4 = 6,
  Basic5to1 = 7,
  Enhanced10to2 = 8,
  Enhanced10to5 = 9,
  Enhanced10to10 = 10,
  Enhanced10to20 = 11,
  Enhanced20to5 = 12,
  Enhanced20to10 = 13,
  Enhanced20to20 = 14,
  Enhanced20to40 = 15,
  Enhanced40to10 = 16,
  Enhanced40to20 = 17,
}

export interface LicenseLimits {
  maxLabs: number;
  maxUsers: number;
  maxDevices: number;
}

export const LICENSE_LIMITS: Record<LicenseType, LicenseLimits> = {
  [LicenseType.None]: {
    maxLabs: 0,
    maxUsers: 0,
    maxDevices: 0,
  },
  [LicenseType.Basic]: {
    maxLabs: 3,
    maxUsers: 10,
    maxDevices: 3,
  },
  [LicenseType.Ogi]: {
    maxLabs: Infinity,
    maxUsers: Infinity,
    maxDevices: Infinity,
  },
  [LicenseType.Custom1]: {
    maxLabs: 4,
    maxUsers: 10,
    maxDevices: 4,
  },
  [LicenseType.Custom2]: {
    maxLabs: 5,
    maxUsers: 10,
    maxDevices: 5,
  },
  [LicenseType.Custom3]: {
    maxLabs: 10,
    maxUsers: 10,
    maxDevices: 10,
  },
  [LicenseType.Custom4]: {
    maxLabs: 30,
    maxUsers: 10,
    maxDevices: 30,
  },
  [LicenseType.Basic5to1]: {
    maxLabs: 1,
    maxUsers: 5,
    maxDevices: 1,
  },
  [LicenseType.Enhanced10to2]: {
    maxLabs: 2,
    maxUsers: 10,
    maxDevices: 2,
  },
  [LicenseType.Enhanced10to5]: {
    maxLabs: 5,
    maxUsers: 10,
    maxDevices: 5,
  },
  [LicenseType.Enhanced10to10]: {
    maxLabs: 10,
    maxUsers: 10,
    maxDevices: 10,
  },
  [LicenseType.Enhanced10to20]: {
    maxLabs: 20,
    maxUsers: 10,
    maxDevices: 20,
  },
  [LicenseType.Enhanced20to5]: {
    maxLabs: 5,
    maxUsers: 20,
    maxDevices: 5,
  },
  [LicenseType.Enhanced20to10]: {
    maxLabs: 10,
    maxUsers: 20,
    maxDevices: 10,
  },
  [LicenseType.Enhanced20to20]: {
    maxLabs: 20,
    maxUsers: 20,
    maxDevices: 20,
  },
  [LicenseType.Enhanced20to40]: {
    maxLabs: 40,
    maxUsers: 20,
    maxDevices: 40,
  },
  [LicenseType.Enhanced40to10]: {
    maxLabs: 10,
    maxUsers: 40,
    maxDevices: 10,
  },
  [LicenseType.Enhanced40to20]: {
    maxLabs: 20,
    maxUsers: 40,
    maxDevices: 20,
  },
};

export const LicenseDisplay: Record<LicenseType, string> = {
  [LicenseType.None]: 'None',
  [LicenseType.Basic]: 'Basic',
  [LicenseType.Ogi]: 'OGI',
  [LicenseType.Custom1]: 'Custom 1',
  [LicenseType.Custom2]: 'Custom 2',
  [LicenseType.Custom3]: 'Custom 3',
  [LicenseType.Custom4]: 'Custom 4',
  [LicenseType.Basic5to1]: 'Basic 5:1',
  [LicenseType.Enhanced10to2]: 'Enhanced 10:2',
  [LicenseType.Enhanced10to5]: 'Enhanced 10:5',
  [LicenseType.Enhanced10to10]: 'Enhanced 10:10',
  [LicenseType.Enhanced10to20]: 'Enhanced 10:20',
  [LicenseType.Enhanced20to5]: 'Enhanced 20:5',
  [LicenseType.Enhanced20to10]: 'Enhanced 20:10',
  [LicenseType.Enhanced20to20]: 'Enhanced 20:20',
  [LicenseType.Enhanced20to40]: 'Enhanced 20:40',
  [LicenseType.Enhanced40to10]: 'Enhanced 40:10',
  [LicenseType.Enhanced40to20]: 'Enhanced 40:20',
};

// Narrows an arbitrary number (e.g. a parsed form value) to a valid LicenseType,
// relying on the numeric enum's reverse mapping. NaN and out-of-range values fail.
export const isLicenseType = (value: number): value is LicenseType =>
  LicenseType[value] !== undefined;

// Licence types a user can assign to an organisation (create and edit flows).
// Derived from the enum, excluding None (an org must always hold a real licence)
// and Ogi (internal-only). New Custom types are picked up automatically.
export const SELECTABLE_LICENSE_TYPES: LicenseType[] = Object.values(
  LicenseType,
)
  .filter((value): value is LicenseType => typeof value === 'number')
  .filter((type) => type !== LicenseType.None && type !== LicenseType.Ogi);

// Dropdown options for the selectable licence types, keyed by enum value.
export const licenseTypeOptions = (): { key: LicenseType; value: string }[] =>
  SELECTABLE_LICENSE_TYPES.map((type) => ({
    key: type,
    value: LicenseDisplay[type],
  }));
